import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Badge,
  TextStyle,
  Heading,
  Button
} from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  SuperTable,
  TableContainer,
  getDateTimeFormat,
  getTranslatedText
} from '@nile/components';
import { SVGIcon } from '@superops/super-icons';
import { useLazyQuery } from '@apollo/client';
import { RMMClient } from '@nile/graphql';
import AssetIconCell from '@nile/banyan/src/Components/Tables/AssetIconCell';
import { PATCH_COLOR_MAP } from '@nile/rmm/src/constants';
import { GET_ALERT_OCCURRENCES } from '@nile/rmm/src/modules/alert-management/Graphql/alert';
import dayjs from 'dayjs';

// Types
interface AlertAsset {
  name: string;
  assetId: string;
  assetClass?: {
    classId: string;
  };
  client?: {
    name: string;
    accountId: string;
  };
  site?: {
    name: string;
    id: string;
  };
}

interface AlertPolicy {
  name: string;
  policyType?: string;
}

interface AlertOccurrence {
  occurrenceId: string;
  occurrenceTime: string;
  description: string;
}

interface AlertDetails {
  id: string;
  message: string;
  severity: string;
  status: string;
  description?: string;
  createdTime?: string;
  resolvedTime?: string;
  externalSource?: string | null;
  asset?: AlertAsset;
  policy?: AlertPolicy;
  occurrenceCount?: number;
}

interface AlertOccurrencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  alertDetails: AlertDetails;
  /** Whether to show action buttons (Resolve, Delete). Defaults to true */
  showActionBtns?: boolean;
  /** Callback when Resolve button is clicked */
  onResolve?: (alertId: string) => void;
  /** Callback when Delete button is clicked */
  onDelete?: (alertId: string) => void;
  /** Resolving alert loading state */
  resolvingAlert?: boolean;
  /** Deleting alert loading state */
  deletingAlert?: boolean;
}

// Cell props interface for table cell components
interface CellProps {
  cell: {
    row?: {
      original?: AlertOccurrence;
    };
    value?: string;
  };
}

// Occurrence Time Cell Component
const OccurrenceTimeCell = ({ cell }: CellProps) => {
  const time = cell?.row?.original?.occurrenceTime || cell?.value;
  const { MSPDateFormat, MSPtimeFormat } = getDateTimeFormat();

  const formattedTime = useMemo(() => {
    if (!time) return '';
    const formatted = dayjs(time).format(`${MSPDateFormat} ${MSPtimeFormat}`);
    return formatted === 'Invalid Date' ? time : formatted;
  }, [time, MSPDateFormat, MSPtimeFormat]);

  return <TextStyle>{formattedTime}</TextStyle>;
};

// Description Cell Component
const DescriptionCell = ({ cell }: CellProps) => {
  const description = cell?.row?.original?.description || cell?.value;
  return <TextStyle>{description}</TextStyle>;
};

// Column cell configuration
const columnsConfig = {
  minWidth: 30,
  maxWidth: 500,
  width: 200,
  OCCURRENCE_TIME_CELL: OccurrenceTimeCell,
  DESCRIPTION_CELL: DescriptionCell
};

// Context for SuperTable
const tableContext = {
  viewConditions: null,
  records: [],
  setRecords: () => {}
};

function AlertOccurrencesModal({
  isOpen,
  onClose,
  alertDetails,
  showActionBtns = true,
  onResolve,
  onDelete,
  resolvingAlert,
  deletingAlert
}: AlertOccurrencesModalProps): React.ReactElement {
  const intl = useIntl();
  const { MSPDateFormat, MSPtimeFormat } = getDateTimeFormat();

  const [listInfo, setListInfo] = useState({
    page: 1,
    pageSize: 15,
    totalCount: 0,
    sort: [
      {
        attribute: 'occurrenceTime',
        order: 'ASC'
      }
    ]
  });

  // API integration for fetching occurrences
  const [fetchOccurrences, { data, loading, error }] = useLazyQuery(
    GET_ALERT_OCCURRENCES,
    {
      client: RMMClient,
      fetchPolicy: 'network-only'
    }
  );

  // Fetch occurrences when modal opens or pagination changes
  useEffect(() => {
    if (isOpen && alertDetails?.id) {
      fetchOccurrences({
        variables: {
          alertId: alertDetails.id,
          listInfo: {
            page: listInfo.page,
            pageSize: listInfo.pageSize,
            sort: listInfo.sort
          }
        }
      });
    }
  }, [
    isOpen,
    alertDetails?.id,
    listInfo.page,
    listInfo.pageSize,
    listInfo.sort,
    fetchOccurrences
  ]);

  // Update listInfo when API response is received
  useEffect(() => {
    if (data?.getAlertOccurrences?.listInfo) {
      const apiListInfo = data.getAlertOccurrences.listInfo;
      setListInfo((prev) => ({
        ...prev,
        totalCount: apiListInfo.totalCount || 0
      }));
    }
  }, [data]);

  // Get occurrences from API response
  const occurrences: AlertOccurrence[] =
    data?.getAlertOccurrences?.alertOccurrences || [];

  // Column definitions
  const getColumnDef = () => {
    return [
      {
        header: getTranslatedText(intl, 'rmm.alert.occurrences.column.time'),
        accessorKey: 'occurrenceTime',
        minSize: 150,
        maxSize: 250,
        size: 200,
        Header: getTranslatedText(intl, 'rmm.alert.occurrences.column.time'),
        accessor: 'occurrenceTime',
        id: 'occurrenceTime',
        type: 'OCCURRENCE_TIME_CELL',
        enableResizing: true,
        disableSortBy: false,
        disableFilters: true,
        field: { name: 'occurrenceTime' },
        width: 200
      },
      {
        header: getTranslatedText(intl, 'common.description'),
        accessorKey: 'description',
        minSize: 300,
        maxSize: 800,
        size: 500,
        Header: getTranslatedText(intl, 'common.description'),
        accessor: 'description',
        id: 'description',
        type: 'DESCRIPTION_CELL',
        enableResizing: true,
        disableSortBy: true,
        disableFilters: true,
        width: 500
      }
    ];
  };

  // Refresh table function - handles pagination changes (server-side pagination)
  const refreshTable = useCallback((ctx: any, updatedListInfo: any) => {
    if (updatedListInfo) {
      setListInfo((prev) => ({
        ...prev,
        page: updatedListInfo.page || prev.page,
        pageSize: updatedListInfo.pageSize || prev.pageSize,
        sort: updatedListInfo.sort || prev.sort
      }));
    }
  }, []);

  // Handle Resolve button click
  const handleResolve = useCallback(() => {
    if (onResolve && alertDetails?.id) {
      onResolve(alertDetails.id);
    }
  }, [onResolve, alertDetails?.id]);

  // Handle Delete button click
  const handleDelete = useCallback(() => {
    if (onDelete && alertDetails?.id) {
      onDelete(alertDetails.id);
    }
  }, [onDelete, alertDetails?.id]);

  const {
    message,
    severity,
    status,
    asset,
    policy,
    resolvedTime,
    occurrenceCount
  } = alertDetails;

  // Check if alert is already resolved
  const isResolved = status === 'Resolved';

  // Format resolved time - returns original string if not a valid date (e.g., "NA")
  const formattedResolvedTime = useMemo(() => {
    if (!resolvedTime) return '';
    const formatted = dayjs(resolvedTime).format(
      `${MSPDateFormat} ${MSPtimeFormat}`
    );
    return formatted === 'Invalid Date' ? resolvedTime : formatted;
  }, [resolvedTime, MSPDateFormat, MSPtimeFormat]);

  return (
    <Modal open={isOpen} onClose={onClose} large showCloseButton={false}>
      <div className="tw-p-4">
        {/* Header Section with Title, Badges, and Action Buttons */}
        <div className="flex fx-sb actr tw-mb-4">
          {/* Left side - Title and Badges */}
          <div className="flex actr tw-gap-3">
            <Heading element="h2">{message}</Heading>
            <Badge status={PATCH_COLOR_MAP[severity] || 'info'} size="large">
              {severity}
            </Badge>
            <Badge status={status === 'Open' ? 'info' : 'success'}>
              {status}
            </Badge>
          </div>

          {/* Right side - Action Buttons and Close */}
          <div className="flex actr tw-gap-2">
            {showActionBtns && (
              <>
                {!isResolved && (
                  <Button
                    onClick={handleResolve}
                    icon={<SVGIcon name="Check" />}
                    loading={resolvingAlert}
                  >
                    <FormattedMessage
                      id="asset.alertViews.resolvebtn"
                      defaultMessage="Resolve"
                    />
                  </Button>
                )}
                <Button
                  onClick={handleDelete}
                  icon={<SVGIcon name="Delete" />}
                  loading={deletingAlert}
                >
                  <FormattedMessage
                    id="common.delete"
                    defaultMessage="Delete"
                  />
                </Button>
              </>
            )}
            <div
              className="ptr tw-ml-2"
              onClick={onClose}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onClose()}
            >
              <SVGIcon name="Close" />
            </div>
          </div>
        </div>

        {/* Alert Details Row */}
        <div className="flex actr tw-gap-4 tw-mb-4 wrap">
          {asset?.name && (
            <div className="flex actr tw-gap-1">
              <AssetIconCell
                cell={{
                  row: { original: { classId: asset?.assetClass?.classId } }
                }}
                iconSize="md"
              />
              <TextStyle weight="medium">{asset.name}</TextStyle>
            </div>
          )}

          <div className="flex actr tw-gap-1">
            <TextStyle variation="subdued">
              {getTranslatedText(
                intl,
                'rmm.alert.occurrences.column.occurrences'
              )}
            </TextStyle>
            <TextStyle weight="medium">
              {occurrenceCount || listInfo.totalCount}
            </TextStyle>
          </div>

          {asset?.client?.name && (
            <div className="flex actr tw-gap-1">
              <TextStyle variation="subdued">
                {getTranslatedText(intl, 'asset.details.client.pl')}
              </TextStyle>
              <TextStyle weight="medium">{asset.client.name}</TextStyle>
            </div>
          )}

          {asset?.site?.name && (
            <div className="flex actr tw-gap-1">
              <TextStyle variation="subdued">
                {getTranslatedText(intl, 'asset.details.site.pl')}
              </TextStyle>
              <TextStyle weight="medium">{asset.site.name}</TextStyle>
            </div>
          )}

          {policy?.name && (
            <div className="flex actr tw-gap-1">
              <TextStyle variation="subdued">
                {getTranslatedText(intl, 'entity.policy')}
              </TextStyle>
              <TextStyle weight="medium">{policy.name}</TextStyle>
            </div>
          )}

          {resolvedTime && (
            <div className="flex actr tw-gap-1">
              <TextStyle variation="subdued">
                {getTranslatedText(intl, 'attribute.alertResolvedTime')}
              </TextStyle>
              <TextStyle weight="medium">{formattedResolvedTime}</TextStyle>
            </div>
          )}
        </div>

        {/* Table Section */}
        <div className="overflow-y-auto vh-365">
          <TableContainer>
            <SuperTable
              noBulkSelect
              context={tableContext}
              columns={[
                {
                  Header: getTranslatedText(
                    intl,
                    'rmm.alert.occurrences.table.header'
                  ),
                  header: 'alert-occurrences',
                  columns: getColumnDef()
                }
              ]}
              data={occurrences}
              defaultColumnCells={columnsConfig}
              listInfo={listInfo}
              rowCount={listInfo.pageSize}
              padded="sm"
              isLoading={loading}
              refreshTable={refreshTable}
              columnWidthPersonalization={false}
              sortingPersonalization={false}
            />
          </TableContainer>
        </div>
      </div>
    </Modal>
  );
}

export default AlertOccurrencesModal;
